import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType } from '@/features/api/directus';
import {
  susDetailFields,
  type SUSDetail,
  type SusPage,
  type SUSQueryArg,
  type SUSRow,
  type SUSSaveArg,
} from '@/models/sus.type';
import { triggerFlow } from '@directus/sdk';

// service des urgences (SUS)
const SUS_FLOW_READ_ID = 'cf732578-3199-484c-a118-e8492a89fc10';
const SUS_FLOW_SAVE_ID = 'd01acd08-21c1-4497-9be4-3d8a643d3966';
const SUS_FLOW_DELETE_ID = 'b0343f3c-d36c-4c66-b300-48a902315253';

const susApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['sus'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getSus: builder.query<SusPage, SUSQueryArg>({
        queryFn: async ({ page, pageSize, fields, orderBy, filter }) => {
          try {
            const data = await directus.request<SusPage>(
              triggerFlow('GET', SUS_FLOW_READ_ID, {
                page: String(page),
                pageSize: String(pageSize),
                ...(fields?.length ? { fields: fields.join(',') } : {}),
                ...(orderBy ? { orderBy } : {}),
                ...(filter ? { filter: filter as unknown as string } : {}),
              }),
            );
            return { data };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },

        providesTags: ['sus'],
      }),
      // Même flow de lecture, filtré sur l'id : renvoie le SU complet pour l'édition.
      getSusById: builder.query<SUSDetail, number>({
        queryFn: async id => {
          try {
            const page = await directus.request<{ rows: SUSDetail[] }>(
              triggerFlow('GET', SUS_FLOW_READ_ID, {
                page: '1',
                pageSize: '1',
                fields: susDetailFields.join(','),
                filter: { id: { _eq: id } } as unknown as string,
              }),
            );
            const sus = page.rows[0];
            if (!sus)
              return { error: { errors: [{ message: 'SU introuvable' }] } as DirectusErrorType };
            return { data: sus };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
        providesTags: (_result, _error, id) => [{ type: 'sus', id }],
      }),
      // Même flow en POST : sans `id` = création, avec `id` = mise à jour.
      saveSus: builder.mutation<SUSRow, SUSSaveArg>({
        queryFn: async body => {
          try {
            const data = await directus.request<SUSRow>(
              triggerFlow('POST', SUS_FLOW_SAVE_ID, body as unknown as Record<string, string>),
            );
            return { data };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
        invalidatesTags: ['sus'],
      }),
      // Suppression d'un ou plusieurs SU par leurs ids.
      deleteSus: builder.mutation<void, number[]>({
        queryFn: async ids => {
          try {
            await directus.request(
              triggerFlow('POST', SUS_FLOW_DELETE_ID, { ids } as unknown as Record<string, string>),
            );
            return { data: undefined };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
        invalidatesTags: ['sus'],
      }),
    }),
  });

export const { useGetSusQuery, useGetSusByIdQuery, useSaveSusMutation, useDeleteSusMutation } =
  susApi;
